import { useQuery, UseQueryOptions } from "react-query";
import { fetchCategories, BASE_CATEGORY_SLUG } from "lib/api";
import { getUniqueChildCategories } from "lib/utils";

const useAllCategoriesWithChildren = (extraOpts?: UseQueryOptions) => {
  const Q = {
    slugs: BASE_CATEGORY_SLUG,
    sparams: { include: "children" },
  };

  return useQuery(
    ["categories", Q],
    () => fetchCategories(Q.slugs, Q.sparams),
    extraOpts
  );
};

const useSingleCategoryFromAccount = (
  accountUrl,
  category = BASE_CATEGORY_SLUG
) => {
  const Q = {
    slugs: category,
    sparams: {
      accountUrls: [accountUrl],
    },
  };

  return useQuery(
    ["categories", Q],
    () => fetchCategories(category, Q.sparams),
    {
      enabled: !!accountUrl,
      select: (data) => data[0],
    }
  );
};

const useCategories = (
  categories,
  searchParams?,
  extraOpts?: UseQueryOptions
) => {
  const Q = {
    slugs: categories,
    sparams: searchParams,
  };

  return useQuery(
    ["categories", Q],
    () => fetchCategories(categories, searchParams),
    extraOpts
  );
};

type ChildCategoryOpts = {
  onlySlugs: boolean;
  categories: string;
};

type Category = {
  _id: string;
  description?: string;
  descriptionParsed: { data: {}; content: string };
  name: string;
  slug: string;
  children?: {};
  images: {
    background: string | null;
    card: string | null;
  };
  account: {
    _id: string;
    name: string;
    urlName: string;
  };
};

function useChildrenCategories<T extends boolean>(
  onlySlugs: T,
  categories = BASE_CATEGORY_SLUG
) {
  const Q = getChildrenCategoriesQuery(categories);

  type RetType = T extends true ? string[] : Category[];
  return useQuery<RetType>(
    ["categories", Q],
    () => fetchCategories(categories, Q.sparams),
    {
      select: (data) =>
        onlySlugs
          ? (getUniqueChildCategories(data).map((cg) => cg.slug) as RetType)
          : (getUniqueChildCategories(data) as RetType),
    }
  );
}

const getChildrenCategoriesQuery = (categories = BASE_CATEGORY_SLUG) => {
  const Q = {
    slugs: categories,
    sparams: {
      include: "children",
    },
  };
  return Q;
};

export {
  useAllCategoriesWithChildren,
  useCategories,
  useChildrenCategories,
  useSingleCategoryFromAccount,
  getUniqueChildCategories,
  getChildrenCategoriesQuery,
};
